import { keepPreviousData, useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getGames, getGame, getGameByTitle, getGameMusic,
	getCsrf, getMe, postLogin, postLogout, postRegister, postGameAccess,
	getGameOwnerships, getSubscriptionPlans, getSubscriptions, postCancelSubscription,
	postGameCheckout, postSubscriptionChange, postSubscriptionCheckout, getPlayHistory,
	getTournamentHistory, getPocketPlaySurvey, savePocketPlaySurvey
 } from './api_funcs';

const keys = {
  all: ['games'],
  list: (p) => ['games', 'list', p],
  detail: (id) => ['games', 'detail', id],
  music: (id) => ['games', 'music', id],

  csrf: ['auth', 'csrf'],
  me: ['auth', 'me'],

  gameAccess: ['game', 'access'],
  gameCheckout: ['game', 'checkout'],
  gameOwnerships: ['game', 'ownerships'],
  playHistory: ['game', 'play-history'],
  tournamentHistory: ['game', 'tournament-history'],
  subscriptionPlans: ['subscriptions', 'plans'],
  subscriptions: ['subscriptions', 'subscriptions'],
  survey: ['survey', 'pocketplay'],
};

export function useGames(params) {
  return useQuery({
    queryKey: keys.list(params),
    queryFn: () => getGames(params),
    placeholderData: keepPreviousData, // smooth numbered pagination
  });
}

export function useGame(id) {
  return useQuery({
    queryKey: keys.detail(id),
    queryFn: () => getGame(id),
    enabled: !!id,
  });
}

export function useGameByTitle(title) {
  return useQuery({
	queryKey: keys.list({ title }),
	queryFn: () => getGameByTitle(title),
	enabled: !!title,
    staleTime: 5 * 60_000,
    refetchOnWindowFocus: false,
  });
}

export function useGameMusic(gameId) {
  return useQuery({
    queryKey: keys.music(gameId),
    queryFn: () => getGameMusic(gameId),
    enabled: !!gameId,
    staleTime: 5 * 60_000,
    refetchOnWindowFocus: false,
  });
}

export function useSubscriptionPlans(params = {}) {
  const { enabled = true, ...queryParams } = params;
  return useQuery({
	queryKey: [...keys.subscriptionPlans, queryParams],
	queryFn: () => getSubscriptionPlans(queryParams),
	enabled,
  });
}

export function useSubscriptions(params = {}) {
  const { enabled = true, ...queryParams } = params;
  return useQuery({
	queryKey: [...keys.subscriptions, queryParams],
	queryFn: () => getSubscriptions(queryParams),
	enabled,
  });
}

export function useGameOwnerships(params = {}) {
  const { enabled = true, ...queryParams } = params;
  return useQuery({
	queryKey: [...keys.gameOwnerships, queryParams],
	queryFn: () => getGameOwnerships(queryParams),
	enabled,
  });
}

export function usePlayHistory({ enabled = true } = {}) {
  return useQuery({
    queryKey: keys.playHistory,
    queryFn: getPlayHistory,
    enabled,
    staleTime: 60_000,
  });
}

export function useTournamentHistory({ enabled = true } = {}) {
  return useQuery({
    queryKey: keys.tournamentHistory,
    queryFn: getTournamentHistory,
    enabled,
    staleTime: 60_000,
  });
}

export function usePocketPlaySurvey({ enabled = true } = {}) {
  return useQuery({
    queryKey: keys.survey,
    queryFn: getPocketPlaySurvey,
    enabled,
    refetchOnWindowFocus: false,
  });
}

export function useSavePocketPlaySurvey() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: savePocketPlaySurvey,
    onSuccess: (data) => qc.setQueryData(keys.survey, data),
  });
}

// ---- auth hooks ----
export const useBootstrapCsrf = () =>
  useQuery({ queryKey: keys.csrf, queryFn: getCsrf, staleTime: Infinity });

export const useMe = () =>
  useQuery({ queryKey: keys.me, queryFn: getMe, staleTime: 5 * 60_000 });

export const useLogin = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ identifier, password }) => postLogin({ identifier, password }),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.me }),
  });
};

export const useLogout = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => postLogout(),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.me }),
  });
};

export const useRegister = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ username, email, password1, password2 }) =>
      postRegister({ username, email, password1, password2 }),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.me }),
  });
};

// ---- Game Hooks ----
export const useGameAccess = () => {
  return useMutation({
	mutationFn: postGameAccess,
  });
};

export const useGameCheckout = () => {
  return useMutation({
	mutationFn: postGameCheckout,
  });
};

export const useSubscriptionCheckout = () => {
  return useMutation({
	mutationFn: postSubscriptionCheckout,
  });
};

export const useCancelSubscription = () => {
  const qc = useQueryClient();
  return useMutation({
	mutationFn: postCancelSubscription,
	onSuccess: () => {
	  qc.invalidateQueries({ queryKey: keys.subscriptions });
	  qc.invalidateQueries({ queryKey: keys.all });
	},
  });
};

export const useSubscriptionChange = () => {
  const qc = useQueryClient();
  return useMutation({
	mutationFn: postSubscriptionChange,
	onSuccess: () => {
	  qc.invalidateQueries({ queryKey: keys.subscriptions });
	  qc.invalidateQueries({ queryKey: keys.all });
	},
  });
};
